import React, { useEffect, useState } from 'react';
import './HeroSection.css';

const HeroSection = () => {
  const [contourData, setContourData] = useState([]);
  const [elevationPoints, setElevationPoints] = useState([]);

  useEffect(() => {
    // Generate realistic contour lines (like a topographic map)
    const contours = [
      { path: "M 20,200 C 50,180 80,220 120,200 S 180,160 220,190 S 280,220 320,200 S 360,180 380,195", elevation: "100m" },
      { path: "M 25,185 C 60,165 90,205 130,185 S 190,145 230,175 S 290,205 330,185 S 365,165 375,180", elevation: "110m" },
      { path: "M 30,170 C 70,150 100,190 140,170 S 200,130 240,160 S 300,190 340,170 S 370,150 380,165", elevation: "120m" },
      { path: "M 35,155 C 80,135 110,175 150,155 S 210,115 250,145 S 310,175 350,155 S 375,135 380,150", elevation: "130m" },
      { path: "M 40,140 C 90,120 120,160 160,140 S 220,100 260,130 S 320,160 360,140", elevation: "140m" },
      { path: "M 45,125 C 100,105 130,145 170,125 S 230,85 270,115 S 330,145 370,125", elevation: "150m" },
      { path: "M 60,110 C 110,90 140,130 180,110 S 240,70 280,100 S 340,130 360,110", elevation: "160m" },
      { path: "M 80,95 C 120,75 150,115 190,95 S 250,55 290,85 S 350,115 360,95", elevation: "170m" },
      { path: "M 100,80 C 130,60 160,100 200,80 S 260,40 300,70", elevation: "180m" },
      { path: "M 130,65 C 150,45 180,85 220,65 S 280,25 310,55", elevation: "190m" },
      { path: "M 170,50 C 190,30 220,70 260,50", elevation: "200m" },
    ];

    setContourData(contours);

    // Generate elevation points
    const points = [
      { x: 100, y: 100, elevation: "145.2" },
      { x: 180, y: 80, elevation: "175.8" },
      { x: 250, y: 60, elevation: "195.3" },
      { x: 150, y: 150, elevation: "128.7" },
      { x: 220, y: 130, elevation: "165.4" },
      { x: 300, y: 100, elevation: "182.1" },
      { x: 80, y: 180, elevation: "112.6" },
      { x: 200, y: 180, elevation: "145.9" },
      { x: 320, y: 140, elevation: "168.3" },
      { x: 120, y: 220, elevation: "98.5" },
    ];
    setElevationPoints(points);
  }, []);

  return (
    <section className="hero-section" id="home">
      <div className="hero-background">
        <div className="bg-grid"></div>
        <div className="bg-glow glow-1"></div>
        <div className="bg-glow glow-2"></div>
      </div>

      <div className="hero-content">
        <div className="hero-badge">
          <span className="badge-dot"></span>
          AutoCAD Plugin for Surveyors
        </div>
        
        <h1 className="hero-title">
          Transform Survey Data into
          <span className="highlight">Precision Contour Maps</span>
        </h1>
        
        <p className="hero-description">
          Generate professional contour maps, calculate volumes, and convert KML⇄CAD 
          with CivilCADPro - The complete survey toolkit for AutoCAD.
        </p>
        
        <div className="hero-buttons">
          <a href="#demo" className="book-demo-button">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="3" y="5" width="14" height="12" rx="2" stroke="white" strokeWidth="2"/>
              <line x1="3" y1="9" x2="17" y2="9" stroke="white" strokeWidth="2"/>
              <line x1="7" y1="13" x2="9" y2="13" stroke="white" strokeWidth="2"/>
              <line x1="11" y1="13" x2="13" y2="13" stroke="white" strokeWidth="2"/>
            </svg>
            Book a Demo
          </a>
        </div>
        
        <div className="hero-stats">
          <div className="stat-item">
            <span className="stat-number">11</span>
            <span className="stat-label">Survey Tools</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">1000+</span>
            <span className="stat-label">Happy Users</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">99.9%</span>
            <span className="stat-label">Accuracy</span>
          </div>
        </div>

        <div className="hero-features-mini">
          <span className="mini-feature">✓ Contour Maps</span>
          <span className="mini-feature">✓ Volume Calc</span>
          <span className="mini-feature">✓ KML⇄CAD</span>
        </div>
      </div>
      
      <div className="hero-visual">
        <div className="map-container">
          <svg viewBox="0 0 400 400" className="contour-map-svg">
            {/* Map border */}
            <rect x="10" y="10" width="380" height="380" rx="8" fill="#f0fdf4" stroke="#10B981" strokeWidth="2"/>
            
            {/* Map grid */}
            {[...Array(8)].map((_, i) => (
              <line key={`vg-${i}`} x1={10 + i*55} y1="10" x2={10 + i*55} y2="390" stroke="#dcfce7" strokeWidth="0.5"/>
            ))}
            {[...Array(8)].map((_, i) => (
              <line key={`hg-${i}`} x1="10" y1={10 + i*55} x2="390" y2={10 + i*55} stroke="#dcfce7" strokeWidth="0.5"/>
            ))}
            
            {/* Contour lines */}
            {contourData.map((contour, index) => (
              <g key={`contour-${index}`}>
                <path
                  d={contour.path}
                  fill="none"
                  stroke={index < 6 ? "#10B981" : "#059669"}
                  strokeWidth={index % 3 === 0 ? 2 : 1.2}
                  strokeOpacity={0.6 + (index * 0.04)}
                  className="contour-line"
                  style={{ animationDelay: `${index * 0.3}s` }}
                />
                {index % 2 === 0 && (
                  <text
                    x={100 + index * 5}
                    y={140 - index * 8}
                    fontSize="7"
                    fill="#059669"
                    className="elevation-label"
                    style={{ animationDelay: `${index * 0.3 + 0.5}s` }}
                  >
                    {contour.elevation}
                  </text>
                )}
              </g>
            ))}
            
            {/* Elevation points */}
            {elevationPoints.map((point, index) => (
              <g key={`point-${index}`} className="elevation-point-group" style={{ animationDelay: `${index * 0.1}s` }}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="2.5"
                  fill="#10B981"
                  className="elevation-point"
                />
                <text
                  x={point.x + 5}
                  y={point.y - 5}
                  fontSize="6.5"
                  fill="#047857"
                  className="elevation-text"
                >
                  {point.elevation}
                </text>
              </g>
            ))}
            
            {/* Legend */}
            <g className="map-legend">
              <rect x="285" y="350" width="100" height="35" rx="4" fill="white" stroke="#10B981" strokeWidth="0.5"/>
              <text x="290" y="362" fontSize="7" fill="#047857" fontWeight="bold">CONTOUR MAP</text>
              <text x="290" y="374" fontSize="6" fill="#059669">Contour Interval: 10m</text>
            </g>
            
            {/* North arrow */}
            <g className="north-arrow">
              <line x1="30" y1="30" x2="30" y2="15" stroke="#047857" strokeWidth="1.5"/>
              <polygon points="30,10 27,18 33,18" fill="#047857"/>
              <text x="25" y="8" fontSize="6" fill="#047857" fontWeight="bold">N</text>
            </g>
            
            {/* Scale bar */}
            <g className="scale-bar">
              <line x1="30" y1="380" x2="130" y2="380" stroke="#047857" strokeWidth="1"/>
              <line x1="30" y1="377" x2="30" y2="383" stroke="#047857" strokeWidth="1"/>
              <line x1="130" y1="377" x2="130" y2="383" stroke="#047857" strokeWidth="1"/>
              <text x="70" y="375" fontSize="6" fill="#047857" textAnchor="middle">0-100m</text>
            </g>
          </svg>
          
          {/* Map title overlay */}
          <div className="map-title-overlay">
            <span className="map-title">SURVEY PLOT - CONTOUR MAP</span>
            <span className="map-subtitle">Scale 1:1000 | Units: Meters</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;